import { Component, TemplateRef, OnInit, ViewChild } from '@angular/core';
import { EventLogInfo, SensorQueryParameterInfo, Event, EventType, SensorGroup, Sensor, SensorReadingType, UserAccessRole, UserGroup, ExportDataCriteria, InterpolationInterval, InterpolationMethod, ExportSaveAs, ExportData, TimeZoneDisplay as TimeZoneSelection, SensorReadingCount } from '../../shared/models';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { SensorService } from '../../shared/services/sensor.service';
import { SensorGroupService } from '../../shared/services/sensorgroup.service';
import moment from 'moment';
import { SensorReadingsService } from './sensor-readings.service';
import { ChartFavoriteInfo } from '../../shared/models/ChartFavorite/chartfavoriteInfo.model';
import { UserService } from '../../shared/services/user.service';
import { AppComponent } from '../../app.component';
import { ChartFavoriteRequest } from '../../shared/models/ChartFavorite/chartFavoriteRequest.model';
import { ChartDetails } from '../../shared/models/ChartFavorite/chartDetails.model';
import { ChartReadingsEchartModalComponent } from '../review-readings/chart-readings-echart-modal/chart-readings-echart-modal.component';
import { ActivatedRoute } from '@angular/router';
import { TriggerService } from '../trigger-settings/trigger.service';
import { EventLogSearchCriteria } from '../../shared/models/EventLog/EventLogSearchCriteria.model';
import { EventMapper } from '../trigger-settings/trigger.mapping';
import { SensorReadingInfo } from '../../shared/models/EventLog/SensorReadingInfo.model';
import { Message, MessageActionType, MessageType, AppComponentName } from '../../shared/models/Messages/message.model';
import { AppMessageService } from '../../shared/services/message.service';
import { Charting } from 'app/shared/classes/charting';
import { CommonShared } from 'app/shared/classes/common';
import { Authorization } from 'app/shared/classes/authorization';
import { DateRanges } from '@models/ranges';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from '../messages/changelog-notes/changelognotes.components';
import { ConfirmationService } from 'primeng/api';


// The page's "last used" selection is persisted to localStorage using the same
// ChartFavoriteRequest structure that saved favorites use, so auto-restore on
// launch recovers the complete chart state (including ChartDetails). A saved
// favorite is remembered by its id (> 0); an ad-hoc selection uses id -1.


// The favorite's legacy single-axis manual scaling columns, migrated onto the
// new per-axis scaling for backwards compatibility.
interface LegacyManualScaling {
  isManual: boolean;
  min: number;
  max: number;
}


@Component({
  standalone: false,
  selector: 'app-sensor-readings',
  templateUrl: './sensor-readings.component.html',
  styleUrls: ['./sensor-readings.component.scss']
})

export class SensorReadingsComponent implements OnInit {

  @ViewChild('chartRef') chartRef: any;
  @ViewChild('echartRef') echartRef: ChartReadingsEchartModalComponent;
  private charting = new Charting();
  public common = new CommonShared();
  public auth = new Authorization();

  modalDragOn: boolean = false;

  public username = 'unknown';
  public favoriteName = '';
  public chartFavorites: ChartFavoriteInfo[] = [];
  public chartFavoritesNamesToDisplay: { id: number; name: string; userName: string; toGroupBy: string}[] = [];
  public selectedChartFavorite: any;
  public favoriteButtonLabel = 'Make Favorite';
  public disableFavoriteButton = false;
  public userGroups: UserGroup[] = [];
  public sensorGroups: SensorGroup[] = [];
  public readySensorGroups = false;
  public sensorsDetails: Sensor[] = [];
  public sensorReadingTypeDetails: SensorReadingType[] = [];
  public selectedSensorGroup: any;
  public selectedSensors: Sensor[] = [];
  public selectedDataTypes: SensorReadingType[] = [];

  // Stable references for the eChart inputs so the template does not create a new
  // array / Date each change-detection cycle (which would loop the child's
  // ngOnChanges and freeze the page).
  private _echartSensorGroups: any[] = [];
  private _echartSensorGroupsRef: any;
  get echartSensorGroups(): any[] {
    if (this.selectedSensorGroup !== this._echartSensorGroupsRef) {
      this._echartSensorGroupsRef = this.selectedSensorGroup;
      this._echartSensorGroups = this.selectedSensorGroup ? [this.selectedSensorGroup] : [];
    }
    return this._echartSensorGroups;
  }

  private _echartDateRangeStart: Date;
  private _echartDateRangeStartMs: number;
  get echartDateRangeStart(): Date {
    const start = this.selectedDateRange?.startDate;
    if (start == undefined) {
      return undefined;
    }
    const ms = start.valueOf();
    if (ms !== this._echartDateRangeStartMs) {
      this._echartDateRangeStartMs = ms;
      this._echartDateRangeStart = start.toDate();
    }
    return this._echartDateRangeStart;
  }
  public chartOutputSelected: boolean = false;
  public selectedRange: any = null;
  private rangeJustClicked = false;   // set by rangeClicked, consumed by datesUpdated
  public preEditObjectAsJSONString = '';
  public changeLogNotes = '';
  public chartFavoritesLoaded: boolean = false;
  public associatedUserGroups: UserGroup[] = [];
  public isReadyAssociatedUserGroups = false;
  public selectedUserGroup: UserGroup;
  public exportDataCriteria: ExportDataCriteria;
  public reloadSensorReadings = false;
  public enableChartAndExportButtons: boolean = false;

  public sensorReadingCountReady: boolean = false;
  public sensorReadingCount: number = 0;
  public sensorReadingCounts: SensorReadingCount[];
  public maxChartReadingCount: number = 122000;
  public sensorReadingCountMsg: string = "Counts > 100K may result in poor charting performance or unable to chart.";

  public makefavoriteChartData: ChartFavoriteRequest = {
    id: undefined,
    name: undefined,
    isManualScaling: undefined,
    scaleYMin: undefined,
    scaleYMax: undefined,
    outlierYMin: undefined,
    outlierYMax: undefined,
    sensorGroupID: undefined,
    sensorGroupIDs: [],
    sensorIDs: [],
    sensorReadingTypeIDs: [],
    dateRange: undefined,
    chartDetails: undefined,
    userName: undefined,
    userGroupId: null
  };

  // Saved eChart options for a restored favorite, bound to the eChart component
  // (which applies them to itself once). Set from a favorite's ChartDetails JSON.
  public savedChartDetails: ChartDetails = null;

  public datatypes: string[] = [];
  public sensorReadingInfos: SensorReadingInfo[] = [];
  public sensorDataParams: SensorQueryParameterInfo;

  public chartOptions: any = {
    showXAxis: true,
    showYAxis: true,
    gradient: false,
    showLegend: true,
    showXAxisLabel: true,
    xAxisLabel: 'Date-Time (Select Range from the below Scale)',
    showYAxisLabel: true,
    yAxisLabel: 'Value',
    axisFormat: null,
    autoScale: true,
    scaleYMin: null,
    scaleYMax: null,
    legendTitle: 'Sensors & Reading Types',
    timeline: true,
    colorScheme: {
      domain: [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ]
    }
  };

  public chartData: any[] = [];
  public view: any[] = [1200, 720];

  // Event-action marker series overlaid on the eChart (populated by the
  // "Overlay Event Actions" button and passed to the eChart component).
  public eventOverlaySeries: { name: string; series: { name: any; value: any }[] }[] = [];

  // eChart dark-background toggle (bound to the reusable eChart component).
  public echartDarkBackground: boolean = false;
  private static readonly ECHART_DARK_BACKGROUND_STORAGE_KEY = 'EchartDarkBackground';

  // Auto-restore of the last-used selection on launch.
  private static readonly LAST_USED_SETTINGS_KEY = 'SensorReadingsLastUsed';
  private hasRouteFavoriteId = false;   // a favorite id in the URL wins over restore
  private lastUsedRestoreDone = false;  // restore runs at most once
  public datatypeDropDownSettings: any = {
    singleSelection: false,
    idField: 'id',
    textField: 'type',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 5,
    allowSearchFilter: true
  };
  public chartOutlierYMin: number;
  public chartOutlierYMax: number;

  logModalRef: BsModalRef;
  modalRef: BsModalRef;
  exportOptionsModalRef: BsModalRef;

  config = {
    backdrop: false,
    ignoreBackdropClick: true
  };
  public isModalOpen = false;

  public sensorDropDownSettings: any = {
    singleSelection: false,
    idField: 'id',
    textField: 'displayedSensorName',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 2,
    allowSearchFilter: false,

  };

  public userGroupconfig: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select User Group',
    noResultsFound: 'No User Groups found!',
    searchOnKey: 'name'
  };

  public chartOverlayLoaded = true;
  public events: Event[] = [];
  public eventLogs: EventLogInfo[] = [];
  public chartDate: any;
  public selectedDateRange: any;
  alwaysShowCalendars: boolean;
  public userNameTimer: any;

  ranges = DateRanges;
  invalidDates: moment.Moment[] = [];

  public globalSwitch: boolean;
  public selectedTimeZone: TimeZoneSelection = TimeZoneSelection.Local;
  public selectedTimeZoneOffset: number = moment().utcOffset();
  public showOriginalReadings: boolean = false;
  public showCalibratedReadings: boolean = true;

  constructor(public toastr: ToastrService, public modalService: BsModalService, private _triggerService: TriggerService, public _appMessageService: AppMessageService,
    public _sensorService: SensorService, public _sensorGroupService: SensorGroupService, private global: AppComponent, private confirmationService: ConfirmationService, 
    public _userService: UserService, public _sensorReadingService: SensorReadingsService, private route: ActivatedRoute) {
    this.alwaysShowCalendars = true;
    this.globalSwitch = global.globalSwitch;
  }

  isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));

  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        
        if (this.username != null && this.username != '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.loadSavedSettings();
    this.getAssociatedUserGroups();
    //this.getUserGroups();
    this.getSensorGroupWithSensorsAndReadingTypes();

    this.exportDataCriteria = {
      filename: 'exportData',
      saveAs: ExportSaveAs.CSV2,
      startDate: moment().startOf('day').format('YYYY-MM-DD HH:mm'),
      endDate: moment().endOf('day').format('YYYY-MM-DD HH:mm'),
      sensorReadingTypes: [],
      interpolateData: false,
      interpolateInterval: InterpolationInterval.I30,
      interpolateIntervalValue: 30,
      interpolateMethod: InterpolationMethod.Linear,
      ignoreOutliers: false,
      ignoreOutliersLessThan: undefined,
      ignoreOutliersGreaterThan: undefined,
      uuids: [],
      retrieveRawValues: false,
      includeGpsInfo: false,
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset,
      includeCalValues: this.showCalibratedReadings,
      includeOrigValues: this.showOriginalReadings
    };
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  loadSavedSettings() {
    var showOriginalReadingsStr = localStorage.getItem('ShowOriginalReadings');
    var showCalibratedReadingsStr = localStorage.getItem('ShowCalibratedReadings');
    var timeZoneSelectedStr = localStorage.getItem('TimeZoneSelected');
    
    [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(timeZoneSelectedStr);
    this.showCalibratedReadings = (showCalibratedReadingsStr === 'true') ? true : false;
    this.showOriginalReadings = (showOriginalReadingsStr === '' || showOriginalReadingsStr == undefined || showOriginalReadingsStr === 'true') ? true : false;

    this.echartDarkBackground = localStorage.getItem(SensorReadingsComponent.ECHART_DARK_BACKGROUND_STORAGE_KEY) === 'true';
  }


  // Persist the eChart dark-background preference when the user toggles it.
  onEchartDarkBackgroundChange() {
    localStorage.setItem(SensorReadingsComponent.ECHART_DARK_BACKGROUND_STORAGE_KEY, this.echartDarkBackground ? 'true' : 'false');
    this.saveLastUsedSettings();
  }


  // Re-persist the "last used" snapshot when the user changes a chart option in
  // the eChart component (chart type, aggregate toggles, colors, buckets, axes,
  // ignore-outlier bounds), so those choices survive a page reload.
  onEchartOptionsChanged() {
    this.saveLastUsedSettings();
  }


  getAssociatedUserGroups(): void {
    this._userService.getAssociatedUserGroups(this.username).subscribe(
      results => {
        if (results != null) {
          this.isReadyAssociatedUserGroups = true;
          this.associatedUserGroups = results;
          this.getUserGroups();
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  getSensorGroupWithSensorsAndReadingTypes(): void {
    this._sensorGroupService.getSensorGroupWithSensorsAndReadingTypes().subscribe(
      results => {
        if (results != null) {
          this.sensorGroups = results;
          //this.sensorGroups = this.sensorGroups.sort((a, b) => a.name.toString().localeCompare(b.name.toString(), 'en', { numeric: true }));
          this.readySensorGroups = true;
          this.tryRestoreLastUsed();
        } else {
          this.toastr.warning('No sensor groupnames found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  getUserGroups(): void {
    this._userService.getUserGroups().subscribe(
      results => {
        if (results != null) {
          this.userGroups = results;
          this.getChartFavorites();
        } else {
          this.toastr.warning('No user groups found');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving all user groups');
      });
  }


  getSensorReadings(params: SensorQueryParameterInfo): void {
    this._sensorService.getSensorReadings(params).subscribe(
      results => {
        if (results != null) {
          this.sensorReadingInfos = results;
          this.reloadSensorReadings = false;
          this.chartData = [];
          if (this.chartOutputSelected) {
            this.updateChartData();
          }
        } else {
          this.toastr.warning('No sensor readings found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor readings.');
      });
  }


  getSensorReadingCount() {
    if (this.selectedSensorGroup.id != undefined && this.selectedSensors.length > 0) {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null) {
        this.sensorDataParams = {
          sensorGroupId: this.selectedSensorGroup.id,
          startDate: this.selectedDateRange.startDate.startOf('day').format('YYYY-MM-DD HH:mm'),
          endDate: this.selectedDateRange.endDate.endOf('day').format('YYYY-MM-DD HH:mm'),
          retrieveRawValues: false,
          timeZoneDisplay: this.selectedTimeZone,
          timeZoneOffset: this.selectedTimeZoneOffset
        }
      }
    }

    this.sensorReadingCountReady = false;

    this._sensorService.getSensorReadingCount(this.sensorDataParams).subscribe(
      results => {
        if (results != null) {
          try {
            this.sensorReadingCounts = results;
            this.updateReadingCountUsingCurrentSelections();
            this.sensorReadingCountReady = true;
          }
          catch { }
        } else {
          this.toastr.warning('No sensor readings found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor reading count.');
      });
  }


  updateReadingCountUsingCurrentSelections() {
    this.sensorReadingCount = 0;

    if (this.sensorReadingCounts == undefined || this.sensorReadingCounts.length < 1) {
      return;
    }

    this.selectedSensors.forEach(sensor => {
      this.selectedDataTypes.forEach(readingType => {
        var sensorStats = this.sensorReadingCounts.find(x => x.sensorId == sensor.id);

        if (sensorStats != undefined) {
          var readingTypeCount = sensorStats.readingTypeCount.find(x => x.id == readingType.id);

          if (readingTypeCount != undefined) {
            this.sensorReadingCount += readingTypeCount.count;
          }
        }
      });
    });
  }


  getRandomColor() {
    const color = Math.floor(0x1000000 * Math.random()).toString(16);
    return '#' + ('000000' + color).slice(-6);
  }


  sensorGroupSelectionChanged(): void {
    this.chartOutputSelected = false;
    this.sensorReadingInfos = [];
    this.enableChartAndExportButtons = false;
    this.sensorReadingCount = 0;

    this.selectedSensors = [];
    this.selectedDataTypes = [];
    this.selectedDateRange = '';

    this.chartData = [];
    this.chartData = [...this.chartData];
    this.sensorReadingTypeDetails = [];

    if (this.selectedSensorGroup) {
      this.sensorsDetails = this.selectedSensorGroup.sensors;
      this.sensorsDetails.forEach(s => s.displayedSensorName = (s.name != undefined && s.name != '') ? s.uuid + '  (' + s.name + ')' : s.uuid );
      this.sensorsDetails.sort((x, y) => (x.displayedSensorName == y.displayedSensorName) ? 0 : x.displayedSensorName ? 1 : -1);
    }

    this.updateReadingCountUsingCurrentSelections();
  }


  sensorSelectionChanged(): void {
    this.setSensorReadingDetails();

    for (let i = 0; i < this.selectedSensors.length; i++ ) {
      this.selectedSensors[i] = this.selectedSensorGroup.sensors.find(s => s.id == this.selectedSensors[i].id);
    }

    if (this.selectedDateRange != '') {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null) {
        if (this.selectedDataTypes != undefined && this.chartOutputSelected) {
          this.chartData = [];
          this.updateChartData();
        }
      }
    }

    this.updateReadingCountUsingCurrentSelections();
  }


  setSensorReadingDetails(): void {
    this.selectedSensors.forEach(sensor => {
      // A favorite created on the Analyze Sensor Readings page can carry sensors
      // that are not in the single group loaded here, so guard the lookup instead
      // of dereferencing undefined.sensorReadingTypes.
      const details = this.sensorsDetails.find(x => x.id == sensor.id);
      if (details == undefined || details.sensorReadingTypes == undefined) {
        return;
      }
      details.sensorReadingTypes.forEach(readingType => {
        if (!this.sensorReadingTypeDetails.map(x => x.type).includes(readingType.type)) {
          this.sensorReadingTypeDetails.push(readingType);
        }
      });
    });
    this.sensorReadingTypeDetails = [...this.sensorReadingTypeDetails];
  }


  sensorDeSelectAllSelectionChanged(sensors: Sensor[]): void {
    this.chartData = [];
    this.chartData = [...this.chartData];

    this.selectedSensors = sensors;
    this.updateReadingCountUsingCurrentSelections();
  }


  sensorSelectAllSelectionChanged(sensors: Sensor[]): void {
    this.selectedSensors = sensors;

    this.selectedSensors = (this.selectedSensorGroup.sensors != null) ? this.selectedSensorGroup.sensors : [] ;
    this.setSensorReadingDetails();
    if (this.selectedDateRange != '') {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null && this.chartOutputSelected) {
        this.updateChartData();
      }
    }

    this.updateReadingCountUsingCurrentSelections();
  }


  sensorDeSelectionChanged(sensors: Sensor[]): void {
    if (this.selectedSensors == undefined) {
      this.chartData = [];
      this.chartData = [...this.chartData];
      this.selectedDataTypes = [];
    }
    this.datatypes = [];

    if (this.chartData.length > 0 && this.selectedDataTypes != undefined && this.chartOutputSelected) {
      this.chartData = [];
      this.updateChartData();
    }
    this.updateReadingCountUsingCurrentSelections();
  }


  updateChartData(): void {
    this.chartOutputSelected = true;

    // Remember this selection so it can be auto-restored on the next launch.
    this.saveLastUsedSettings();

    if (this.reloadSensorReadings) {
      this.loadSensorReadings();
      return;
    }

    for (let i = 0; i < this.selectedSensors.length; i++ ) {
      this.selectedSensors[i] = this.selectedSensorGroup.sensors.find(s => s.id == this.selectedSensors[i].id);
    }

    this.chartData = [];
    if (this.selectedSensors != undefined && this.selectedDataTypes != undefined) {
      this.selectedDataTypes.forEach(datatype => {
        this.selectedSensors.forEach(sensor => {

          const legend = (sensor.name != undefined && sensor.name != '') ? sensor.name : sensor.uuid ;

          if (this.showCalibratedReadings) {
            var sensorReadings = this.sensorReadingInfos.filter(s => s.uuid == sensor.uuid && s.type == datatype.type && s.value != "nan" && s.value != "NaN")
                                                    .map(a => ({ name: new Date(a.createdDate), value: a.value }));

            if (this.chartOutlierYMin != undefined && this.chartOutlierYMin.toString() != '') {
              sensorReadings = sensorReadings.filter(s => Number(s.value) > this.chartOutlierYMin);
            }

            if (this.chartOutlierYMax != undefined && this.chartOutlierYMax.toString() != '') {
              sensorReadings = sensorReadings.filter(s => Number(s.value) < this.chartOutlierYMax);
            }

            const newData = {
              name: datatype.type + ', ' + legend + ' (c)',
              series: sensorReadings,
              uuid: sensor.uuid
            };
            if (newData.series.length > 0) {
              this.chartData.push(newData);
              this.chartData = [...this.chartData];
            }
          }

          if (this.showOriginalReadings) {
            var sensorReadings = this.sensorReadingInfos.filter(s => s.uuid == sensor.uuid && s.type == datatype.type && s.origValue != "nan" && s.origValue != "NaN")
                                                    .map(a => ({ name: new Date(a.createdDate), value: a.origValue }));

            if (this.chartOutlierYMin != undefined && this.chartOutlierYMin.toString() != '') {
              sensorReadings = sensorReadings.filter(s => Number(s.value) > this.chartOutlierYMin);
            }

            if (this.chartOutlierYMax != undefined && this.chartOutlierYMax.toString() != '') {
              sensorReadings = sensorReadings.filter(s => Number(s.value) < this.chartOutlierYMax);
            }

            const newData = {
              name: datatype.type + ', ' + legend + ' (o)',
              series: sensorReadings,
              uuid: sensor.uuid
            };
            if (newData.series.length > 0) {
              this.chartData.push(newData);
              this.chartData = [...this.chartData];
            }
          }

        });
      });
    }
  }


  datesUpdated(selectedDateRange) {
    if (this.rangeJustClicked) {
      this.rangeJustClicked = false;      // trailing event from a preset click; keep selectedRange
    } else {
      this.selectedRange = null;          // genuine custom selection; drop stale preset label
    }

    if (selectedDateRange.startDate != undefined) {
      this.enableChartAndExportButtons = true;
      this.reloadSensorReadings = true;
      this.getSensorReadingCount();
    }
  }


  loadSensorReadings(): void {
    if (!this.selectedSensorGroup) {
      return;
    }

    if (this.selectedSensorGroup.id != undefined && this.selectedSensors.length > 0) {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null) {
        this.sensorDataParams = {
          sensorGroupId: this.selectedSensorGroup.id,
          startDate: this.selectedDateRange.startDate.startOf('day').format('YYYY-MM-DD HH:mm'),
          endDate: this.selectedDateRange.endDate.endOf('day').format('YYYY-MM-DD HH:mm'),
          retrieveRawValues: false,
          timeZoneDisplay: this.selectedTimeZone,
          timeZoneOffset: this.selectedTimeZoneOffset
        };

        this.sensorReadingInfos = [];
        this.getSensorReadings(this.sensorDataParams);
      }
    } else {
      this.chartData = [];
      this.chartData = [...this.chartData];
    }
  }


  datatypeSelectionChanged(): void {
    if (this.selectedSensorGroup != undefined && this.selectedSensors != undefined) {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null && this.chartOutputSelected) {
        this.updateChartData();
      }
    }
    this.updateReadingCountUsingCurrentSelections();
  }


  datatypeSelectAllSelectionChanged(readingTypes: SensorReadingType[]): void {
    this.selectedDataTypes = this.sensorReadingTypeDetails;
    if (this.selectedSensorGroup != undefined && this.selectedSensors != undefined) {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null && this.chartOutputSelected) {
        this.updateChartData();
      }
    }
    this.updateReadingCountUsingCurrentSelections();
  }


  datatypeDeSelectionChanged(): void {
    if (this.selectedSensorGroup != undefined && this.selectedSensors != undefined) {
      if (this.selectedDateRange.startDate != null && this.selectedDateRange.endDate != null && this.chartOutputSelected) {
        this.updateChartData();
      }
    }
    this.updateReadingCountUsingCurrentSelections();
  }


  datatypeDeSelectAllSelectionChanged(readingTypes: SensorReadingType[]): void {
    this.chartData = [];
    this.chartData = [...this.chartData];

    this.selectedDataTypes = readingTypes;
    this.updateReadingCountUsingCurrentSelections();
  }


  exportData(template: TemplateRef<any>) {
    this.exportDataCriteria.startDate = this.selectedDateRange.startDate.format('YYYY-MM-DD HH:mm');
    this.exportDataCriteria.endDate = this.selectedDateRange.endDate.format('YYYY-MM-DD HH:mm');
    this.exportDataCriteria.sensorReadingTypes = this.selectedDataTypes.map(x => x.type);
    this.exportDataCriteria.ignoreOutliers = (this.chartOutlierYMin != undefined || this.chartOutlierYMax != undefined) ? true : false;
    this.exportDataCriteria.ignoreOutliersLessThan = this.chartOutlierYMin;
    this.exportDataCriteria.ignoreOutliersGreaterThan = this.chartOutlierYMax;
    this.exportDataCriteria.timeZoneDisplay = this.selectedTimeZone;
    this.exportDataCriteria.timeZoneOffset = this.selectedTimeZoneOffset;

    for (let i = 0; i < this.selectedSensors.length; i++ ) {
      this.selectedSensors[i] = this.selectedSensorGroup.sensors.find(s => s.id == this.selectedSensors[i].id);
    }
    this.exportDataCriteria.uuids = this.selectedSensors.map(x => x.uuid);

    this.exportOptionsModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  exportDataWithOptions() {
    if (this.exportDataCriteria.filename == '') {
      this.toastr.warning('Please provide a filename for the exported data');
      return;
    }
    this.exportOptionsModalRef.hide();

    this.exportDataCriteria.includeCalValues = this.showCalibratedReadings;
    this.exportDataCriteria.includeOrigValues = this.showOriginalReadings;
    this.exportDataCriteria.retrieveRawValues = this.showOriginalReadings;

    switch (this.exportDataCriteria.interpolateInterval) {
      case InterpolationInterval.I15:
        this.exportDataCriteria.interpolateIntervalValue = 15;
        break;
      case InterpolationInterval.I30:
        this.exportDataCriteria.interpolateIntervalValue = 30;
        break;
      case InterpolationInterval.I60:
        this.exportDataCriteria.interpolateIntervalValue = 60;
        break;
      case InterpolationInterval.I1440:
        this.exportDataCriteria.interpolateIntervalValue = 1440;
        break;
      default:
        this.exportDataCriteria.interpolateIntervalValue = 15;
        break;
    }

    if (this.exportDataCriteria.interpolateData == false) {
      this.exportDataCriteria.interpolateIntervalValue = undefined;
    }

    this._sensorService.exportSensorReadingsAsText(this.exportDataCriteria).subscribe(
      results => {
        if (results != null) {
          if (results.length == 0) {
            this.toastr.warning('No data associated with the given sensors and types');
          }
          else {
            this.saveExportToFile(results);
          }
        } else {
          this.toastr.warning('No sensor readings found to export');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while attempting to export sensor data');
      });
  }


  saveExportToFile(fileContent: string) {
    if (this.exportDataCriteria.saveAs == ExportSaveAs.JSON) {
      this.saveData(fileContent, this.exportDataCriteria.filename + '.json');
    }
    else if (this.exportDataCriteria.saveAs == ExportSaveAs.CSV) {
      this.saveData(fileContent, this.exportDataCriteria.filename + '.csv');
    }
    else if (this.exportDataCriteria.saveAs == ExportSaveAs.CSV2) {
      this.saveData(fileContent, this.exportDataCriteria.filename + '.csv');
    }

    var msg = `Exported ${this.sensorReadingCount} readings from '${this.selectedSensorGroup.name}' to ${this.exportDataCriteria.saveAs} file`;
    if (this.exportDataCriteria.interpolateData) msg += ' with interpolation';
    if (this.exportDataCriteria.includeGpsInfo) msg += ' with GPS info';
    this.createMessageData(MessageActionType.Edited, this.makefavoriteChartData, msg);
  }


  setInterpolateData(state: boolean) {
    this.exportDataCriteria.interpolateData = state;
  }


  saveData(dataString: string, fileName: string) {
    const blob = new Blob(['\uFEFF', dataString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }


  onDeleteChartFavoriteConfirm() {
    this.confirmationService.confirm({
      message: 'Please confirm that you want to delete the current chart favorite',
      key: 'cd1',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.removeChartFavorite();
      },
      reject: () => {
      }
    });
  }


  removeChartFavorite(): void {
    if (this.selectedChartFavorite.name != null && this.selectedChartFavorite.name.length > 0) {
      this._sensorReadingService.deleteChartFavorite(this.selectedChartFavorite.id, this.username).subscribe(
        result => {
          if (result === 'success') {
            this.createMessageData(MessageActionType.Deleted, this.selectedChartFavorite, '');
            this.toastr.success('Chart favorite deleted');
            this.getChartFavorites();
            this.selectedChartFavorite = undefined;
            this.clearChartFavoriteSelection();
          } else {
            this.toastr.warning('No Chart favorites found.');
          }
          return;
        },
        err => {
        });
    }
  }


  setRangeSelected(selectedRange: any) {
    this.selectedRange = selectedRange;
    this.rangeJustClicked = true;
  }


  saveChartFavorite(template: TemplateRef<any>): void {
    if (this.selectedChartFavorite == undefined || this.selectedChartFavorite.userGroupId == undefined || this.selectedChartFavorite.userGroupId < 1) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == this.selectedChartFavorite.userGroupId);
    }

    if (this.isUpdate()) {
      this.favoriteName = this.selectedChartFavorite.name;
    }
    if (!this.modalRef || !this.isModalOpen) {
      this.isModalOpen = true;
      this.modalRef = this.modalService.show(template, this.config);
    }
  }


  addOrUpdateChartFavorite(): void {
    if (this.isUpdate()) {
      this.setChartFavoriteRequest();
      this.updateChartFavorite();
    }
    else {
      if (this.favoriteName.trim()) {
        if (!this.chartFavorites.some(x => x.name === this.favoriteName.trim())) {
          this.setChartFavoriteRequest();
          this.addFavorite();
        } else {
          this.toastr.error('Chart favorite name already exists. Please select a new one');
        }
      } else {
        this.toastr.error('Please enter a valid name');
      }
    }

    this.favoriteName = undefined;
  }


  isUpdate(): boolean {
    if (this.selectedChartFavorite == undefined) {
      return false;
    }

    var userCanEdit = this.selectedChartFavorite.userFeatureAccess["EditChart"];

    return this.selectedChartFavorite.id > 0 && userCanEdit;
  }


  updateChartFavorite(): void {
    this._sensorReadingService.updateChartFavorite(this.makefavoriteChartData, this.username).subscribe(
      result => {
        if (result === 'success') {
          this.createMessageData(MessageActionType.Edited, this.makefavoriteChartData, this.getEditChanges(this.makefavoriteChartData));
          this.modalRef.hide();
          this.isModalOpen = false;
          this.toastr.success('Chart favorite updated successfully.');
          this.getChartFavorites();
          this.clearChartFavoriteSelection();
          this.selectedChartFavorite = undefined;
        } else {
          this.toastr.warning('No Chart favorites found to update.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  addFavorite(): void {
    this._sensorReadingService.createChartFavorite(this.makefavoriteChartData).subscribe(
      result => {
        if (result === 'success') {
          this.createMessageData(MessageActionType.New, this.makefavoriteChartData, '');
          this.modalRef.hide();
          this.isModalOpen = false;
          this.toastr.success('Chart favorite created successfully.');
          this.getChartFavorites();
          this.clearChartFavoriteSelection();
          this.selectedChartFavorite = undefined;
        } else {
          this.toastr.warning('No Chart favorites found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while saving chart favorite');
      }
    );
  }


  setChartFavoriteRequest(): void {
    this.makefavoriteChartData.name = this.favoriteName;

    this.makefavoriteChartData.id = (this.selectedChartFavorite != undefined) ? this.selectedChartFavorite.id : -1;

    this.makefavoriteChartData.userName = this.username;
    this.makefavoriteChartData.userGroupId = (this.selectedUserGroup == undefined || this.selectedUserGroup.id < 0) ? null : this.selectedUserGroup.id;
    // Manual scaling now lives per-axis in ChartDetails; the legacy single-axis
    // columns are no longer written (kept only for reading old favorites).
    this.makefavoriteChartData.isManualScaling = false;
    this.makefavoriteChartData.sensorGroupID = this.selectedSensorGroup.id;
    // Single group today; sent as a one-element array so the plural field is
    // ready for multi-group favorites without a further payload change.
    this.makefavoriteChartData.sensorGroupIDs = [this.selectedSensorGroup.id];
    this.makefavoriteChartData.sensorIDs = this.selectedSensors.map(x => x.id);
    this.makefavoriteChartData.sensorReadingTypeIDs = this.selectedDataTypes.map(x => x.id);

    this.makefavoriteChartData.scaleYMin = null;
    this.makefavoriteChartData.scaleYMax = null;
    this.makefavoriteChartData.outlierYMin = this.chartOutlierYMin;
    this.makefavoriteChartData.outlierYMax = this.chartOutlierYMax;

    this.makefavoriteChartData.dateRange = this.encodeDateRange();
    this.makefavoriteChartData.chartDetails = this.buildChartDetails();
  }


  // Build the ChartDetails JSON string persisted on the favorite: the eChart
  // component's current options merged with the host-level display options
  // (timezone, readings source, dark mode). Returns null if there is nothing to
  // save (no chart rendered yet).
  private buildChartDetails(): string {
    const details: ChartDetails = (this.echartRef != undefined) ? this.echartRef.getChartDetails() : {};

    details.timeZone = this.selectedTimeZone;
    details.timeZoneOffset = this.selectedTimeZoneOffset;
    details.showOriginalReadings = this.showOriginalReadings;
    details.showCalibratedReadings = this.showCalibratedReadings;
    details.darkBackground = this.echartDarkBackground;

    return JSON.stringify(details);
  }


  // Parse a favorite's ChartDetails JSON and apply the host-level options
  // (timezone, reading source, dark mode) directly. The eChart-specific options
  // are handed to the eChart component via the savedChartDetails input, which
  // applies them to itself once the chart is (re)built.
  //
  // Legacy favorites saved before ChartDetails existed have a null/empty/
  // unparseable value: fall back to a line chart and drive the eChart's
  // ignore-outlier bounds from the favorite's dedicated outlier columns (already
  // applied to chartOutlierYMin/Max). The favorite's legacy single-axis manual
  // scaling (isManualScaling/scaleYMin/scaleYMax columns) is migrated onto the
  // primary 'Value' axis of the new per-axis scaling for backwards compatibility.
  private applyChartDetails(chartDetails: string, legacyScaling?: LegacyManualScaling): void {
    this.savedChartDetails = null;

    const details = this.parseChartDetails(chartDetails);

    // Legacy favorite (no usable ChartDetails): assume a line chart and reuse the
    // favorite's legacy outlier columns for the eChart's ignore-outlier bounds.
    if (details == undefined) {
      const saved: ChartDetails = {
        echartType: 'line',
        yMinIgnore: this.outlierToString(this.chartOutlierYMin),
        yMaxIgnore: this.outlierToString(this.chartOutlierYMax)
      };
      this.migrateLegacyManualScaling(saved, legacyScaling);
      this.savedChartDetails = saved;
      return;
    }

    if (details.timeZone != undefined) {
      [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(details.timeZone);
    }
    if (details.showOriginalReadings != undefined) {
      this.showOriginalReadings = details.showOriginalReadings;
    }
    if (details.showCalibratedReadings != undefined) {
      this.showCalibratedReadings = details.showCalibratedReadings;
    }
    if (details.darkBackground != undefined) {
      this.echartDarkBackground = details.darkBackground;
    }

    this.migrateLegacyManualScaling(details, legacyScaling);

    // Hand the eChart-specific options to the eChart component (applied on build).
    this.savedChartDetails = details;
  }


  // Backwards compatibility: when a favorite/last-used snapshot carries the old
  // single-axis manual scaling but no new per-axis map, migrate its bounds onto
  // the primary 'Value' axis so the eChart still honors them.
  private migrateLegacyManualScaling(details: ChartDetails, legacy?: LegacyManualScaling): void {
    if (legacy == undefined || legacy.isManual !== true) {
      return;
    }
    if (details.manualScaleByAxis != undefined && Object.keys(details.manualScaleByAxis).length > 0) {
      return; // new per-axis scaling already present; prefer it
    }
    const min = this.legacyScaleToNumber(legacy.min);
    const max = this.legacyScaleToNumber(legacy.max);
    if (min == null && max == null) {
      return;
    }
    details.manualScaleByAxis = { Value: { min, max } };
  }


  // Coerce a legacy scale bound (possibly string/null/blank) to a finite number
  // or null.
  private legacyScaleToNumber(value: any): number | null {
    if (value == undefined || value.toString().trim() === '') {
      return null;
    }
    const num = Number(value);
    return isNaN(num) ? null : num;
  }


  // Parse a favorite's ChartDetails JSON, or undefined when it is null, empty, or
  // unparseable (i.e. a legacy favorite saved before ChartDetails existed).
  private parseChartDetails(chartDetails: string): ChartDetails | undefined {
    if (chartDetails == undefined || chartDetails === '') {
      return undefined;
    }
    try {
      return JSON.parse(chartDetails) ?? undefined;
    } catch {
      return undefined;
    }
  }


  // The eChart's ignore-outlier bounds are strings (they back text inputs); the
  // legacy outlier columns are numbers. Convert to the string form the eChart
  // expects, or '' when there is no bound.
  private outlierToString(value: number): string {
    return (value == undefined) ? '' : value.toString();
  }


  // Encode the current date range the way favorites store it: a named preset
  // label when one is selected, otherwise a "start,end" ISO string.
  private encodeDateRange(): string {
    if (this.selectedRange != null) {
      return this.selectedRange.label;
    }
    const startDate = this.selectedDateRange.startDate.format('YYYY-MM-DDTHH:mm:ss');
    const endDate = this.selectedDateRange.endDate.format('YYYY-MM-DDTHH:mm:ss');
    return startDate + ',' + endDate;
  }


  // Persist the current selection to localStorage so it can be auto-restored on
  // the next launch. Only persists complete selections. Uses the same
  // ChartFavoriteRequest structure as saved favorites so the full chart state
  // (including ChartDetails) round-trips. A saved favorite keeps its own id so
  // restore can reuse the favorite path; an ad-hoc selection is stored as id -1.
  private saveLastUsedSettings(): void {
    if (!this.isChartFavoriteReadyToSave()) {
      return;
    }

    const settings: ChartFavoriteRequest = {
      id: (this.selectedChartFavorite != undefined) ? this.selectedChartFavorite.id : -1,
      name: (this.selectedChartFavorite != undefined) ? this.selectedChartFavorite.name : undefined,
      userName: this.username,
      userGroupId: (this.selectedChartFavorite != undefined) ? this.selectedChartFavorite.userGroupId : null,
      sensorGroupID: this.selectedSensorGroup.id,
      sensorGroupIDs: [this.selectedSensorGroup.id],
      sensorIDs: this.selectedSensors.map(x => x.id),
      sensorReadingTypeIDs: this.selectedDataTypes.map(x => x.id),
      dateRange: this.encodeDateRange(),
      isManualScaling: false,
      scaleYMin: null,
      scaleYMax: null,
      outlierYMin: this.chartOutlierYMin,
      outlierYMax: this.chartOutlierYMax,
      chartDetails: this.buildChartDetails()
    };

    localStorage.setItem(SensorReadingsComponent.LAST_USED_SETTINGS_KEY, JSON.stringify(settings));
  }


  // Restore the last-used selection once both sensor groups and chart favorites
  // are loaded. Runs at most once, and is skipped when a favorite id was supplied
  // in the URL (that deep link wins).
  private tryRestoreLastUsed(): void {
    if (this.lastUsedRestoreDone || this.hasRouteFavoriteId) {
      return;
    }
    if (!this.readySensorGroups || !this.chartFavoritesLoaded) {
      return;   // wait until both async loads have completed
    }

    this.lastUsedRestoreDone = true;
    this.restoreLastUsedSettings();
  }


  // Read the persisted last-used selection and rehydrate the page from it,
  // auto-rendering the chart. Restores a saved favorite if one was last used,
  // otherwise an ad-hoc selection by id.
  private restoreLastUsedSettings(): void {
    const saved = localStorage.getItem(SensorReadingsComponent.LAST_USED_SETTINGS_KEY);
    if (saved == undefined || saved === '') {
      return;
    }

    let settings: ChartFavoriteRequest;
    try {
      settings = JSON.parse(saved);
    } catch {
      return;   // ignore a malformed snapshot
    }
    if (settings == undefined) {
      return;
    }

    // Last used was a saved favorite that still exists → reuse the favorite path.
    if (settings.id != undefined && settings.id > 0
        && this.chartFavorites.some(f => f.id === settings.id)) {
      this.setChartFavoriteSelection(settings.id);
      this.updateChartData();
      return;
    }

    // Otherwise rehydrate the ad-hoc selection from the stored snapshot.
    const group = this.sensorGroups.find(g => g.id === settings.sensorGroupID);
    if (group == undefined) {
      return;   // the saved group no longer exists
    }

    this.selectedSensorGroup = group;
    this.sensorGroupSelectionChanged();

    const sensorIds = settings.sensorIDs ?? [];
    this.selectedSensors = (this.selectedSensorGroup.sensors ?? []).filter(s => sensorIds.includes(s.id));
    this.sensorSelectionChanged();

    const typeIds = settings.sensorReadingTypeIDs ?? [];
    this.selectedDataTypes = [ ...this.sensorReadingTypeDetails.filter(t => typeIds.includes(t.id)) ];

    this.chartOutlierYMin = settings.outlierYMin;
    this.chartOutlierYMax = settings.outlierYMax;

    // Reuse the favorite restore path for the full chart state (timezone,
    // readings source, dark mode, and the eChart options). Falls back to a line
    // chart with the legacy outlier columns when chartDetails is absent. Any
    // legacy manual scaling is migrated onto the primary axis.
    this.applyChartDetails(settings.chartDetails,
      { isManual: settings.isManualScaling === true, min: settings.scaleYMin, max: settings.scaleYMax });

    if (settings.dateRange != undefined && settings.dateRange !== '') {
      this.applyDateRange(settings.dateRange);
    }

    this.enableChartAndExportButtons = true;
    this.reloadSensorReadings = true;
    this.getSensorReadingCount();
    this.updateChartData();   // auto-render with the restored selection
  }


  // Apply an encoded date range (preset label or "start,end") to selectedRange /
  // selectedDateRange — the inverse of encodeDateRange(). Shared by favorite and
  // last-used restore.
  private applyDateRange(dateRange: string): void {
    if (dateRange != null && this.ranges[dateRange] != undefined) {
      this.selectedRange = { label: dateRange, dates: [] };

      let range: any;
      if (dateRange === 'Today') {
        range = [moment().startOf('day'), moment().endOf('day')];
      } else if (dateRange === 'Yesterday') {
        range = [moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')];
      } else {
        range = this.ranges[dateRange];
      }
      this.selectedDateRange = { startDate: range[0], endDate: range[1] };
    } else {
      const parts: string[] = dateRange.split(',');
      this.selectedRange = null;
      this.selectedDateRange = { startDate: moment(parts[0]), endDate: moment(parts[1]) };
    }
  }


  getChartFavorites(): void {
    this._sensorReadingService.getChartFavorites().subscribe(
      results => {
        if (results != null) {
          this.chartFavoritesLoaded = true;
          this.chartFavorites = results;

          this.chartFavorites.forEach(c => {
            this.auth.mapUserAccessForChart(c, this.globalSwitch, this.username, this.userGroups);
          });

          this.chartFavorites.forEach(c => { if (c.sensorGroup) { c.sensorGroup.sensors = c.sensors; } });
          this.route.params.subscribe(params => {
            if (params['id']) {
              this.hasRouteFavoriteId = true;   // a URL favorite id wins over last-used restore
              this.setChartFavoriteSelection(params['id']);
            }
          });

          this.tryRestoreLastUsed();
        } else {
          this.toastr.warning('No chart favorites found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  setChartFavoriteSelection(id: number): void {
    this.selectedChartFavorite = this.chartFavorites.filter(x => x.id === id)[0];
    this.favoriteSelectionChanged();
  }


  updateChartFavoritesList(showAll: boolean) {
    this.chartFavoritesNamesToDisplay = [];
    this.chartFavorites.forEach(chart => {
      if (chart.userFeatureAccess["ViewListing"] || showAll) {
        this.chartFavoritesNamesToDisplay.push({
          id: chart.id,
          name: chart.name,
          userName: chart.userName,
          toGroupBy: (chart.userGroupId == undefined || chart.userGroupId < 1) ? chart.userName : chart.userGroup
        });
      }
    });

    this.chartFavoritesNamesToDisplay.sort((a, b) => {
      if (b.toGroupBy > a.toGroupBy) {
        return -1;
      }
      if (b.toGroupBy < a.toGroupBy) {
        return 1;
      }
      if (b.name > a.name) {
        return -1;
      }
      if (b.name < a.name) {
        return 1;
      }
    });
  }


  favoriteSelectionChanged(): void {
    this.clearChartFavoriteSelection();
    if (this.selectedChartFavorite == undefined) return;

    this.selectedChartFavorite = this.chartFavorites.filter(x => x.id === this.selectedChartFavorite.id)[0];

    // Chart Group Readings is a single-group page: it can only load a favorite whose
    // sensors all belong to one sensor group. A favorite created on the Analyze Sensor
    // Readings page may reference loose sensors and/or multiple groups, which this page
    // cannot represent - warn and clear the selection instead of loading a partial set.
    const favoriteGroups = (this.selectedChartFavorite.sensorGroups != undefined && this.selectedChartFavorite.sensorGroups.length > 0)
      ? this.selectedChartFavorite.sensorGroups
      : (this.selectedChartFavorite.sensorGroup != undefined ? [this.selectedChartFavorite.sensorGroup] : []);

    const singleGroup = (favoriteGroups.length === 1)
      ? this.sensorGroups.find(g => g.id === favoriteGroups[0].id)
      : undefined;

    const groupSensorIds = (singleGroup?.sensors ?? []).map(s => s.id);
    const allFavoriteSensorIds = (this.selectedChartFavorite.sensors ?? []).map(s => s.id);
    const allSensorsInSingleGroup = singleGroup != undefined
      && allFavoriteSensorIds.every(id => groupSensorIds.includes(id));

    if (!allSensorsInSingleGroup) {
      this.toastr.warning('Cannot load this chart favorite here - its sensors are not all in a single sensor group. Open it from the Analyze Sensor Readings page instead.');
      this.selectedChartFavorite = undefined;
      this.clearChartFavoriteSelection();
      return;
    }

    this.chartOutlierYMin = this.selectedChartFavorite.outlierYMin;
    this.chartOutlierYMax = this.selectedChartFavorite.outlierYMax;

    // Apply saved eChart / display options before readings are loaded so the
    // timezone and reading-source selections drive the query correctly. Any
    // legacy manual scaling on the favorite is migrated onto the primary axis.
    this.applyChartDetails(this.selectedChartFavorite.chartDetails,
      { isManual: this.selectedChartFavorite.isManualScaling, min: this.selectedChartFavorite.scaleYMin, max: this.selectedChartFavorite.scaleYMax });

    const primaryGroupId = this.selectedChartFavorite.sensorGroup?.id ?? this.selectedChartFavorite.sensorGroups?.[0]?.id;
    this.selectedSensorGroup = this.sensorGroups.filter(x => x.id === primaryGroupId)[0];
    this.sensorGroupSelectionChanged();

    // Restrict the favorite's saved sensors to the group actually loaded here. A
    // favorite from the Analyze Sensor Readings page may span multiple groups or
    // loose sensors; only those in this group can be selected. Mirrors the ad-hoc
    // last-used restore path (which filters the group's sensors by saved ids).
    const favoriteSensorIds = (this.selectedChartFavorite.sensorGroup?.sensors ?? []).map(s => s.id);
    this.selectedSensors = (this.selectedSensorGroup?.sensors ?? []).filter(s => favoriteSensorIds.includes(s.id));
    this.sensorSelectionChanged();

    this.selectedDataTypes = this.selectedChartFavorite.sensorReadingTypes;

    this.selectedDataTypes = [... this.selectedDataTypes];

    this.applyDateRange(this.selectedChartFavorite.dateRange);

    this.enableChartAndExportButtons = true;
    this.reloadSensorReadings = true;
    this.getSensorReadingCount();

    this.preEditObjectAsJSONString = JSON.stringify(this.selectedChartFavorite);

    this.saveLastUsedSettings();
  }


  clearChartFavoriteSelection(): void {
    this.selectedSensorGroup = undefined;
    this.selectedSensors = [];
    this.selectedDataTypes = [];
    this.selectedDateRange = '';
    this.selectedRange = null;
    this.rangeJustClicked = false;
    this.chartData = [];
    this.chartData = [...this.chartData];

    this.chartOutlierYMin = undefined;
    this.chartOutlierYMax = undefined;
    this.sensorReadingInfos = [];
    this.enableChartAndExportButtons = false;
    this.sensorReadingCount = 0;
  }


  onFavoriteCancelButton(): void {
    this.modalRef.hide();
    this.isModalOpen = false;
  }


  overlayEventActionsOnChart() {
    this.chartOverlayLoaded = false;
    this.eventOverlaySeries = [];
    this.getEvents();
  }


  getEvents(): void {
    this._triggerService.getEvents('').subscribe(
      results => {
        if (results != null) {
          const eventMapper = new EventMapper();
          var events: Event[] = eventMapper.mapEventInfos2Events(results);
          events.forEach(e =>
            e.sensorConditions.forEach(s => s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID))
          );
          this.getEventLogsForEvent(events);
        } else {
          this.toastr.warning('No Events found');
          this.chartOverlayLoaded = true;
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display');
        this.chartOverlayLoaded = true;
      });
  }


  getEventLogsForEvent(events: Event[]) {
    let countFlag = false;
    const sensorGroupEvent: Event[] = [];
    events.forEach(e => {
      if (e.eventType === EventType.SensorBased && e.sensorConditions !== null) {
        countFlag = false;
        e.sensorConditions.forEach(s => {
          if (s.sensorGroup.id == this.selectedSensorGroup.id && countFlag === false) {
            sensorGroupEvent.push(e);
            countFlag = true;
          }
        });
      }
    });

    if (sensorGroupEvent.length === 0) {
      this.toastr.error('No events found using this sensor group');
      this.chartOverlayLoaded = true;
      return;
    }

    sensorGroupEvent.forEach(event => {
      this.chartOverlayLoaded = false;
      const sensorDataParams: EventLogSearchCriteria = { eventIDs: [event.id], startDate: this.selectedDateRange.startDate.toDate(), endDate: this.selectedDateRange.endDate.toDate(), triggered: false };
      this.getEventLogs(sensorDataParams, event);
    });
  }


  dateUTC(params: EventLogSearchCriteria) {
    params.endDate = moment(params.endDate).add(-(moment().utcOffset()), 'm').toDate();
    params.startDate = moment(params.startDate).add(-(moment().utcOffset()), 'm').toDate();
  }


  getEventLogs(params: EventLogSearchCriteria, event: Event) {
    this.dateUTC(params);
    this._triggerService.getEventLogs(params).subscribe(
      results => {
        if (results != null) {
          const eventLogInfos: EventLogInfo[] = results;
          const logDates: Date[] = [];

          eventLogInfos.forEach(l => {
            if (l.actionResultInfos != null) {
              l.actionResultInfos.forEach(a => {
                if (a.result === '1' || a.result === '11') {
                  logDates.push(new Date(moment.utc(l.createdDate).local().toDate()));
                }
              });
            }
          });

          this.addEventsToChart(logDates, params, event);
        } else {
          this.toastr.warning('No event logs found for ' + event.name);
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving event logs for ' + event.name);
      });
  }


  addEventsToChart(logDates: Date[], eventParams: EventLogSearchCriteria, event: Event) {
    let maxChartValue = 0;
    this.chartData.forEach( chart => {
      const max = Math.max.apply(Math, chart.series.map((o) => o.value));
      if (max > maxChartValue) {
        maxChartValue = max;
      }
    });

    const newSeries: { name; value }[] = [];
    newSeries.push ({name: this.selectedDateRange.startDate.toDate(), value: maxChartValue + 1});
    logDates.forEach( d => {
      const dateB = moment(d).subtract(10, 'minutes').toDate();
      const dateM = moment(d).toDate();
      const dateE = moment(d).add(10, 'minutes').toDate();
      newSeries.push ({name: dateB, value: maxChartValue + 1});
      newSeries.push ({name: dateM, value: maxChartValue + 3});
      newSeries.push ({name: dateE, value: maxChartValue + 1});
    });
    newSeries.push ({name: this.selectedDateRange.endDate.toDate(), value: maxChartValue + 1});

    const newData = {
      name: 'Event (' + event.name + ')',
      series: newSeries
    };

    // Overlay onto the eChart via the overlaySeries input (new array reference
    // so the eChart picks up the change).
    this.eventOverlaySeries = [...this.eventOverlaySeries, newData];

    this.chartOverlayLoaded = true;
  }


  createMessageData(msgActionType: MessageActionType, chart: ChartFavoriteRequest, msgNote: string) {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.Charts,
      actionType: msgActionType,
      appComponentItem: (chart.name != null && chart.name !== '' ? chart.name : this.selectedSensorGroup.name),
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: chart.userGroupId
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


  getEditChanges(chart: ChartFavoriteRequest): string {
    let results = '';
    const original: ChartFavoriteRequest = JSON.parse(this.preEditObjectAsJSONString);

    results += this.common.compareProperty('Name', original.name, chart.name);
    results += this.common.compareProperty('DateRange', original.dateRange, chart.dateRange);

    return results;
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';

    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: 0,
      category: ''
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }

  
  showDataPointsOnChart(chart) {
    this.charting.showDataPointsOnChart(chart);
  }


  selectedTimeZoneChanged() {
    switch (this.selectedTimeZone) {
      case TimeZoneSelection.Local:
        localStorage.setItem('TimeZoneSelected', 'Local');
        this.selectedTimeZoneOffset = moment().utcOffset();
        break;
    
    case TimeZoneSelection.UTC:
      localStorage.setItem('TimeZoneSelected', 'UTC');
        this.selectedTimeZoneOffset = 0;
        break;

    case TimeZoneSelection.Source:
      localStorage.setItem('TimeZoneSelected', 'Source');
        this.selectedTimeZoneOffset = 0;
        break;

      default:
        this.selectedTimeZoneOffset = 0;
        break;
    }

    this.reloadSensorReadings = true;

    // Timezone is a server-side query parameter, so the readings have to be refetched.
    // Auto-refresh only when a chart is already on screen -- otherwise this would render
    // a chart the user never asked for.
    if (this.chartData.length > 0 && this.enableChartAndExportButtons) {
      this.updateChartData();
      return;   // updateChartData() already saved the last-used settings
    }

    this.saveLastUsedSettings();
  }


  isChartFavoriteReadyToSave(): boolean {
    if (this.selectedSensorGroup != undefined &&
        this.selectedSensors != undefined && this.selectedSensors.length > 0 &&
        this.selectedDataTypes != undefined && this.selectedDataTypes.length > 0 &&
        this.selectedDateRange != undefined && this.selectedDateRange.startDate != undefined && this.selectedDateRange.endDate != undefined) {
          return true;
    }
    return false;
  }


  selectedReadingSourceChanged() {
    localStorage.setItem('ShowOriginalReadings', this.showOriginalReadings.toString());
    localStorage.setItem('ShowCalibratedReadings', this.showCalibratedReadings.toString());

    this.reloadSensorReadings = true;
    this.updateChartData();
    this.saveLastUsedSettings();
  }


}
